import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTheme } from '@shared/providers/theme/ThemeContext';
import { useTenantSiteCheck } from '@/hooks/admin/useTenantSiteCheck';
import { AppButton } from '@/components/atoms';

/**
 * Massager (checkin) reset-password expired page.
 *
 * Shown when the token in the email link has expired or no longer
 * matches the password_resets row (e.g. someone requested a fresh
 * link after this one was issued).
 */
const MassagerResetPasswordExpiredPage: React.FC = () => {
  const { site } = useParams<{ site: string }>();
  const navigate = useNavigate();
  const { colors } = useTheme();

  const {
    data: siteCheck,
    isLoading: isCheckingSite,
    isError: isSiteCheckError,
    isUnreachable: isSiteUnreachable,
  } = useTenantSiteCheck(site);

  const validSite =
    !!site &&
    !isCheckingSite &&
    !isSiteCheckError &&
    !!siteCheck?.success &&
    !!siteCheck.data?.exists;

  // Invalid site_type guard — same UX as LoginPage / ForgotPasswordPage.
  // A check with no answer keeps the form (Common network rule); only an answered 「no such site」 lands here.
  if (site && !isCheckingSite && !isSiteUnreachable && (!validSite || isSiteCheckError)) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center overflow-x-hidden bg-[#fa7c64] px-4 py-8">
        <div className="w-full max-w-[420px] rounded-lg bg-white px-6 py-6 text-center shadow-lg">
          <h1 className="mb-3 text-lg font-bold text-red-600">無効なサイト</h1>
          <p className="mb-4 text-sm text-gray-600">指定されたサイトは存在しません。</p>
          <AppButton
            type="primary"
            onClick={() => navigate('/medix/massager/login')}
            style={{ backgroundColor: colors.primary }}
            className="w-full sm:w-auto"
            data-testid="massager-reset-expired-invalid-site-back"
          >
            ログイン画面へ
          </AppButton>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col overflow-x-hidden bg-[#fa7c64]">
      <div className="flex flex-1 flex-col items-center justify-center px-4 py-6 sm:py-10">
        <div className="w-full max-w-[420px]">
          <div className="rounded-lg bg-white px-5 py-6 shadow-lg sm:px-8">
            <div
              className="mb-4 rounded border border-red-600 bg-red-50 px-3 py-2.5 text-left text-sm leading-relaxed text-red-900"
              role="alert"
              data-testid="massager-reset-expired-message"
            >
              パスワードリセット画面のURLの有効期限が切れています
            </div>

            <div className="mt-4 text-center">
              <AppButton
                type="primary"
                onClick={() => navigate(`/${site || 'medix'}/massager/password/reset`)}
                style={{ backgroundColor: colors.primary }}
                className="w-full sm:w-auto"
                data-testid="massager-reset-expired-back"
              >
                パスワードリセットに戻る
              </AppButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MassagerResetPasswordExpiredPage;
